import { type Quaternion, Vector3 } from 'three/webgpu';
import { type PhysicsWorld, RAPIER } from '../physics/PhysicsWorld';

/** Where the camera sits from the eye, in the view's frame (m): to the right, up and back. */
const SHOULDER = 0.6;
const LIFT = 0.25;
const DISTANCE = 3.1;
/** Radius of the sphere cast that keeps the camera out of walls (m). The near plane is 5 cm. */
const PROBE = 0.22;
/** How long the swing between first and third person takes (s). */
const SWING = 0.35;
/** How fast the camera backs out again once whatever pushed it in is gone (1/s). */
const EASE_OUT = 4;
/** Closer than this to the eye (m) the hero is hidden: the camera would be inside it. */
const HIDE_WITHIN = 0.7;

const _right = new Vector3();
const _up = new Vector3();
const _back = new Vector3();
const _dir = new Vector3();
const _shoulder = new Vector3();
const _local = new Vector3();
const _noRotation = { x: 0, y: 0, z: 0, w: 1 };

/**
 * An over-the-shoulder view. The camera sits behind and to the right of the eye and looks
 * the same way, so the crosshair still points where the player aims. A sphere cast from the
 * eye pulls it in before it can go into a wall (snapping in at once, easing back out), and
 * toggling swings it between the eye and the shoulder rather than cutting.
 */
export class ShoulderCamera {
  /** Third person wanted; the camera swings there (or back) over SWING seconds. */
  enabled = false;
  /** How far the camera is from the eye now (m). */
  reach = 0;
  /** 0 = at the eye … 1 = over the shoulder. */
  private blend = 0;
  /** How far behind the shoulder the camera is now (m). */
  private distance = DISTANCE;
  private readonly ball = new RAPIER.Ball(PROBE);

  constructor(
    private readonly physics: PhysicsWorld,
    /** The player's own collider, which the casts ignore. */
    private readonly ignore: RAPIER.Collider,
  ) {}

  /** Whether the hero should be drawn: the camera is well out of its head. */
  get showsHero(): boolean {
    return this.reach > HIDE_WITHIN;
  }

  /**
   * Place the camera for this frame. From the first-person eye (universe) and view
   * orientation, writes the camera's universe position into `out`; the orientation stays
   * the view's.
   */
  place(eye: Vector3, view: Quaternion, dt: number, out: Vector3): Vector3 {
    this.blend = Math.min(1, Math.max(0, this.blend + (this.enabled ? dt : -dt) / SWING));
    const t = this.blend * this.blend * (3 - 2 * this.blend);
    if (t <= 0) {
      this.reach = 0;
      this.distance = DISTANCE;
      return out.copy(eye);
    }
    _right.set(1, 0, 0).applyQuaternion(view);
    _up.set(0, 1, 0).applyQuaternion(view);
    _back.set(0, 0, 1).applyQuaternion(view);
    // Out to the shoulder first (so a wall beside you doesn't swallow the view), then back.
    _dir.copy(_right).multiplyScalar(SHOULDER).addScaledVector(_up, LIFT);
    const side = _dir.length();
    _dir.divideScalar(side);
    _shoulder.copy(eye).addScaledVector(_dir, this.free(eye, _dir, side));
    const free = this.free(_shoulder, _back, DISTANCE);
    // In at once when something's in the way, out gently once it's gone.
    this.distance =
      free < this.distance ? free : this.distance + (free - this.distance) * (1 - Math.exp(-dt * EASE_OUT));
    out.copy(_shoulder).addScaledVector(_back, this.distance);
    out.lerpVectors(eye, out, t);
    this.reach = out.distanceTo(eye);
    return out;
  }

  /** How far a sphere can move from `from` along unit `dir` (up to `max`) before touching anything. */
  private free(from: Vector3, dir: Vector3, max: number): number {
    this.physics.toLocal(from, _local);
    const hit = this.physics.world.castShape(
      _local,
      _noRotation,
      dir,
      this.ball,
      0,
      max,
      true,
      RAPIER.QueryFilterFlags.EXCLUDE_DYNAMIC | RAPIER.QueryFilterFlags.EXCLUDE_SENSORS,
      undefined,
      this.ignore,
    );
    return hit ? Math.max(0, hit.time_of_impact - 0.02) : max;
  }
}
